import { useState, useRef, useEffect, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";

export interface UseDocumentFetchOptions {
  url: string;
  action: "view" | "download";
  filename?: string;
}

export interface UseDocumentFetchResult {
  fetchDocument: (options: UseDocumentFetchOptions) => Promise<void>;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook for fetching and viewing/downloading documents with authentication support.
 * Handles both internal (atmts.io) URLs that require auth and external URLs.
 * Manages blob URL creation and cleanup on unmount.
 */
export function useDocumentFetch(): UseDocumentFetchResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const blobUrlsRef = useRef<string[]>([]);

  // Cleanup blob URLs when component unmounts
  useEffect(() => {
    const blobUrls = blobUrlsRef.current;
    return () => {
      blobUrls.forEach((blobUrl) => {
        window.URL.revokeObjectURL(blobUrl);
      });
    };
  }, []);

  const fetchDocument = useCallback(
    async ({ url, action, filename = "document.pdf" }: UseDocumentFetchOptions) => {
      setIsLoading(true);
      setError(null);

      try {
        const isInternalLink = url.includes("atmts.io");

        if (!isInternalLink) {
          // For external links, use native browser behavior
          if (action === "download") {
            const link = document.createElement("a");
            link.href = url;
            link.download = filename;
            link.click();
          } else {
            window.open(url, "_blank");
          }
          return;
        }

        // For internal links, fetch with authentication
        const token = isAuthenticated ? await getAccessTokenSilently() : undefined;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            Accept: "application/pdf,application/octet-stream,*/*",
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);

        // Track the blob URL for cleanup on component unmount
        blobUrlsRef.current.push(blobUrl);

        // Small delay to ensure the blob URL is fully processed before opening
        await new Promise((resolve) => setTimeout(resolve, 200));

        if (action === "download") {
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = filename;
          link.click();
        } else {
          window.open(blobUrl, "_blank");
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Unknown error occurred";
        console.error(`Error ${action === "download" ? "downloading" : "viewing"} document:`, err);
        setError(new Error(errorMessage));
      } finally {
        setIsLoading(false);
      }
    },
    [getAccessTokenSilently, isAuthenticated],
  );

  return { fetchDocument, isLoading, error };
}

export default useDocumentFetch;
